// src/components/RecommendationsView.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// RECOMMENDATIONS VIEW - Trip Type 6: Destination Recommendations
// Shows AI-suggested destinations when user doesn't know where to go
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import './RecommendationsView.css';

// ═══════════════════════════════════════════════════════════════════════════════
// DESTINATION DATA - Icons, colors, and metadata
// ═══════════════════════════════════════════════════════════════════════════════
const DESTINATION_DATA = {
  // Beach destinations
  'Cancun': { icon: '🏝️', color: 'linear-gradient(135deg, #f59e0b, #d97706)', type: 'beach' },
  'Punta Cana': { icon: '🌴', color: 'linear-gradient(135deg, #10b981, #059669)', type: 'beach' },
  'Turks & Caicos': { icon: '🏝️', color: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', type: 'beach' },
  'Maui': { icon: '🌺', color: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', type: 'beach' },
  'Aruba': { icon: '🏖️', color: 'linear-gradient(135deg, #ec4899, #d946ef)', type: 'beach' },
  'Bahamas': { icon: '🌊', color: 'linear-gradient(135deg, #06b6d4, #0891b2)', type: 'beach' },
  'Maldives': { icon: '🏝️', color: 'linear-gradient(135deg, #0ea5e9, #0284c7)', type: 'beach' },
  'Bali': { icon: '🌴', color: 'linear-gradient(135deg, #22c55e, #16a34a)', type: 'beach' },
  'Thailand': { icon: '🏖️', color: 'linear-gradient(135deg, #eab308, #ca8a04)', type: 'beach' },
  'Hawaii': { icon: '🌺', color: 'linear-gradient(135deg, #f97316, #ea580c)', type: 'beach' },
  'Miami': { icon: '🌴', color: 'linear-gradient(135deg, #14b8a6, #0d9488)', type: 'beach' },
  'Caribbean': { icon: '🏝️', color: 'linear-gradient(135deg, #06b6d4, #0891b2)', type: 'beach' },
  
  // European cities
  'Paris': { icon: '🗼', color: 'linear-gradient(135deg, #6366f1, #4f46e5)', type: 'city' },
  'Rome': { icon: '🏛️', color: 'linear-gradient(135deg, #f59e0b, #d97706)', type: 'city' },
  'Barcelona': { icon: '🏰', color: 'linear-gradient(135deg, #ef4444, #dc2626)', type: 'city' },
  'London': { icon: '🇬🇧', color: 'linear-gradient(135deg, #3b82f6, #2563eb)', type: 'city' },
  'Amsterdam': { icon: '🌷', color: 'linear-gradient(135deg, #f97316, #ea580c)', type: 'city' },
  'Prague': { icon: '🏰', color: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', type: 'city' },
  'Vienna': { icon: '🎭', color: 'linear-gradient(135deg, #ec4899, #db2777)', type: 'city' },
  'Lisbon': { icon: '🚃', color: 'linear-gradient(135deg, #eab308, #ca8a04)', type: 'city' },
  'Greece': { icon: '🏛️', color: 'linear-gradient(135deg, #0ea5e9, #0284c7)', type: 'city' },
  'Italy': { icon: '🍝', color: 'linear-gradient(135deg, #22c55e, #16a34a)', type: 'city' },
  
  // Adventure destinations
  'Costa Rica': { icon: '🦜', color: 'linear-gradient(135deg, #22c55e, #16a34a)', type: 'adventure' },
  'New Zealand': { icon: '🏔️', color: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', type: 'adventure' },
  'Iceland': { icon: '🌋', color: 'linear-gradient(135deg, #6366f1, #4f46e5)', type: 'adventure' },
  'Switzerland': { icon: '🏔️', color: 'linear-gradient(135deg, #ef4444, #dc2626)', type: 'adventure' },
  'Norway': { icon: '🌌', color: 'linear-gradient(135deg, #0ea5e9, #0284c7)', type: 'adventure' },
  'Peru': { icon: '🏔️', color: 'linear-gradient(135deg, #f59e0b, #d97706)', type: 'adventure' },
  'Safari': { icon: '🦁', color: 'linear-gradient(135deg, #eab308, #ca8a04)', type: 'adventure' },
  'Kenya': { icon: '🦁', color: 'linear-gradient(135deg, #22c55e, #16a34a)', type: 'adventure' },
  'Tanzania': { icon: '🐘', color: 'linear-gradient(135deg, #f97316, #ea580c)', type: 'adventure' },
  
  // Asian destinations
  'Japan': { icon: '🗾', color: 'linear-gradient(135deg, #ef4444, #dc2626)', type: 'culture' },
  'Tokyo': { icon: '🗼', color: 'linear-gradient(135deg, #f43f5e, #e11d48)', type: 'city' },
  'Vietnam': { icon: '🍜', color: 'linear-gradient(135deg, #eab308, #ca8a04)', type: 'culture' },
  'Singapore': { icon: '🏙️', color: 'linear-gradient(135deg, #ef4444, #dc2626)', type: 'city' },
  'Dubai': { icon: '🏙️', color: 'linear-gradient(135deg, #f59e0b, #d97706)', type: 'luxury' },
  'India': { icon: '🕌', color: 'linear-gradient(135deg, #f97316, #ea580c)', type: 'culture' },
  
  // Default
  'default': { icon: '✈️', color: 'linear-gradient(135deg, #6366f1, #4f46e5)', type: 'general' }
};

// Get destination metadata
const getDestinationMeta = (name) => {
  // Try exact match first
  if (DESTINATION_DATA[name]) return DESTINATION_DATA[name];
  
  // Try partial match
  const key = Object.keys(DESTINATION_DATA).find(k => 
    name.toLowerCase().includes(k.toLowerCase()) ||
    k.toLowerCase().includes(name.toLowerCase())
  );
  
  return key ? DESTINATION_DATA[key] : DESTINATION_DATA['default'];
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const RecommendationsView = ({
  recommendationsData = {},
  message = '',
  onClose,
  onSelectDestination,
  onSearchFlights
}) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  
  // Parse recommendations from the AI message or data
  const parseRecommendations = () => {
    // If we have structured recommendations data
    if (recommendationsData.destinations && Array.isArray(recommendationsData.destinations)) {
      return recommendationsData.destinations.slice(0, 5);
    }
    
    // If we have a comparison
    if (recommendationsData.comparison && Array.isArray(recommendationsData.comparison)) {
      return recommendationsData.comparison.slice(0, 5);
    }
    
    // Try to parse from message
    if (message) {
      const destinations = [];
      
      // Look for numbered list patterns: "1. Cancun" or "1) Cancun"
      const numberedPattern = /(?:\d+[\.\)]\s*)([A-Z][a-zA-Z\s&']+?)(?:\s*[-–—:]|\s*\(|\n|$)/g;
      let match;
      while ((match = numberedPattern.exec(message)) !== null && destinations.length < 5) {
        const name = match[1].trim();
        if (name.length > 2 && name.length < 30) {
          destinations.push({ name });
        }
      }
      
      // If no numbered list, look for bold patterns: **Cancun**
      if (destinations.length === 0) {
        const boldPattern = /\*\*([A-Z][a-zA-Z\s&',]+?)\*\*/g;
        while ((match = boldPattern.exec(message)) !== null && destinations.length < 5) {
          const name = match[1].trim();
          if (name.length > 2 && name.length < 30 && !name.includes('Why') && !name.includes('Match')) {
            destinations.push({ name });
          }
        }
      }
      
      return destinations;
    }
    
    return [];
  };
  
  const recommendations = parseRecommendations();
  const isComparison = recommendationsData.isComparison || recommendations.length === 2;
  
  // Extract additional info from message for each destination
  const getDestinationDetails = (destName) => {
    const meta = getDestinationMeta(destName);
    
    // Try to find details in the message
    let details = {
      name: destName,
      icon: meta.icon,
      color: meta.color,
      type: meta.type,
      matchScore: null,
      temperature: null,
      flightTime: null,
      highlights: [],
      estimatedPrice: null,
      whyGood: ''
    };
    
    // Look for this destination's section in the message
    if (message) {
      const msgLower = message.toLowerCase();
      const destLower = destName.toLowerCase();
      const destIndex = msgLower.indexOf(destLower);
      
      if (destIndex !== -1) {
        // Get text after destination name (next 500 chars or until next destination)
        const afterDest = message.substring(destIndex, destIndex + 500);
        
        // Look for temperature
        const tempMatch = afterDest.match(/(\d{2,3})[°]?\s*[°]?F/i);
        if (tempMatch) details.temperature = `${tempMatch[1]}°F`;
        
        // Look for flight time
        const flightMatch = afterDest.match(/(\d+\.?\d*)\s*(?:hour|hr|h)\s*(?:flight)?/i);
        if (flightMatch) details.flightTime = `${flightMatch[1]}h flight`;
        
        // Look for price estimate
        const priceMatch = afterDest.match(/\$[\d,]+/);
        if (priceMatch) details.estimatedPrice = priceMatch[0];
        
        // Look for match percentage
        const matchMatch = afterDest.match(/(\d{2,3})%\s*match/i);
        if (matchMatch) details.matchScore = parseInt(matchMatch[1]);
      }
    }
    
    // Generate highlights based on destination type
    if (!details.highlights.length) {
      switch (meta.type) {
        case 'beach':
          details.highlights = ['🏖️ Beautiful beaches', '☀️ Warm weather'];
          break;
        case 'city':
          details.highlights = ['🏛️ Rich culture', '🍽️ Great food'];
          break;
        case 'adventure':
          details.highlights = ['🏔️ Adventure activities', '🌿 Nature'];
          break;
        case 'culture':
          details.highlights = ['🏛️ Historic sites', '🎭 Cultural experiences'];
          break;
        case 'luxury':
          details.highlights = ['✨ Luxury experiences', '🏨 World-class hotels'];
          break;
        default:
          details.highlights = ['✈️ Great destination'];
      }
    }
    
    return details;
  };
  
  // Handle explore click - triggers flight search
  const handleExplore = (destination) => {
    if (onSearchFlights) {
      onSearchFlights(destination);
    } else if (onSelectDestination) {
      onSelectDestination(destination);
    }
  };
  
  // If no recommendations parsed, show the message directly
  if (recommendations.length === 0) {
    return (
      <div className="rv-container">
        <div className="rv-header">
          <div className="rv-header-icon">🌍</div>
          <div>
            <h2 className="rv-header-title">Destination Ideas</h2>
            <p className="rv-header-subtitle">Let me help you find the perfect destination</p>
          </div>
        </div>
        
        <div className="rv-message-box">
          <div className="rv-message-text">{message || "Tell me more about what you're looking for!"}</div>
        </div>
        
        <div className="rv-prompt-box">
          <div className="rv-prompt-icon">💡</div>
          <div className="rv-prompt-text">
            Try asking things like:
            <ul>
              <li>"Where should I go for a beach vacation?"</li>
              <li>"Best places to visit in Europe"</li>
              <li>"Bali or Thailand - which is better?"</li>
              <li>"Somewhere warm in December under $2000"</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="rv-container">
      {/* Header */}
      <div className="rv-header">
        <div className="rv-header-icon">{isComparison ? '⚖️' : '🌍'}</div>
        <div>
          <h2 className="rv-header-title">
            {isComparison ? 'Destination Comparison' : 'Recommended Destinations'}
          </h2>
          <p className="rv-header-subtitle">
            {isComparison 
              ? `Comparing ${recommendations.map(r => r.name).join(' vs ')}`
              : `Based on your preferences • ${recommendations.length} destinations found`
            }
          </p>
        </div>
      </div>
      
      {/* Recommendations Grid */}
      <div className={`rv-grid ${isComparison ? 'comparison' : ''}`}>
        {recommendations.map((rec, idx) => {
          const details = getDestinationDetails(rec.name || rec);
          const isHovered = hoveredCard === idx;
          
          return (
            <div 
              key={idx}
              className={`rv-card ${isHovered ? 'hovered' : ''} ${isComparison ? 'comparison-card' : ''}`}
              onMouseEnter={() => setHoveredCard(idx)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Card Image/Header */}
              <div 
                className="rv-card-image"
                style={{ background: details.color }}
              >
                <div className="rv-card-icon">{details.icon}</div>
                {details.matchScore && (
                  <div className="rv-card-badge">{details.matchScore}% Match</div>
                )}
                {isComparison && (
                  <div className="rv-card-vs">Option {idx + 1}</div>
                )}
              </div>
              
              {/* Card Content */}
              <div className="rv-card-content">
                <h3 className="rv-card-title">{details.name}</h3>
                
                <div className="rv-card-meta">
                  {details.temperature && (
                    <span className="rv-card-meta-item">🌡️ {details.temperature}</span>
                  )}
                  {details.flightTime && (
                    <span className="rv-card-meta-item">✈️ {details.flightTime}</span>
                  )}
                </div>
                
                <div className="rv-card-highlights">
                  {details.highlights.map((h, i) => (
                    <span key={i} className="rv-highlight">{h}</span>
                  ))}
                </div>
                
                {/* Price & CTA */}
                <div className="rv-card-footer">
                  <div className="rv-card-price">
                    {details.estimatedPrice ? (
                      <>
                        <span className="rv-price-from">from</span>
                        <span className="rv-price-amount">{details.estimatedPrice}</span>
                      </>
                    ) : (
                      <span className="rv-price-search">Search for prices</span>
                    )}
                  </div>
                  <button 
                    className="rv-card-cta"
                    onClick={() => handleExplore(details)}
                  >
                    Explore →
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* AI Insight Box */}
      {message && (
        <div className="rv-insight-box">
          <div className="rv-insight-icon">💡</div>
          <div className="rv-insight-text">
            {message.length > 300 ? message.substring(0, 300) + '...' : message}
          </div>
        </div>
      )}
      
      {/* Refine CTA */}
      <div className="rv-refine-box">
        <div className="rv-refine-icon">🎯</div>
        <div className="rv-refine-content">
          <div className="rv-refine-title">Want different recommendations?</div>
          <div className="rv-refine-subtitle">Tell me more about your preferences in the chat!</div>
        </div>
        {onClose && (
          <button className="rv-refine-btn" onClick={onClose}>
            Back to Chat →
          </button>
        )}
      </div>
    </div>
  );
};

export default RecommendationsView;
